import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Radio from "components/radio";
import DateBar from "elements/teesheet/DateBar";
import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { cartOptions, holeOptions, playerOptions } from "../../ClientPortal/Home/teeTimeFilters";
import TeeOnPortalFacilities from "../Components/TeeOnPortalFacilities/TeeOnPortalFacilities";
import { IFacilityPortalExtended, useTeeOnPortalContext } from "../TeeOnPortalContext";
import { useTranslation } from "react-i18next";
import "./teeOnPortalHome.scss";
import { IAPIResponse, StatusCode } from "api/protocols";
import { GetCustomerCheckIns, ICheckIns } from "api/rpc/2024-04/customer/teesheet";
import { GetCustomerTeeSheetSettings } from "api/rpc/2024-04/customer/facility";
import { useAppDispatch } from "hooks/redux";
import { showError } from "redux/actions/ui";
import { GetTeeTimesCustomer } from "api/rpc/2022-09/customer/teetime";
import { GetGuestTeeSheetSettings } from "api/rpc/2024-04/guest/facility";
import { GetGuestCheckIns } from "api/rpc/2024-04/guest/teesheet";
import { GetTeeTimesGuest } from "api/rpc/2022-09/guest/teetime";
import { ITeeTime } from "redux/reducers/models/teetime";
import { convertTime, formatDateFromString } from "helpers/Helpers";
import Callout from "components/callout/Callout";
import { CustomerTeeTimeCard } from "components/customerTeeTimeCard/CustomerTeeTimeCard";
import { CancelToken } from "axios";
import useCancelToken from "hooks/useCancelToken/useCancelToken";
import LoaderCard from "components/loaderCard/LoaderCard";
import { useHistory } from "react-router";
import { LockTeeTime } from "api/rpc/2024-04/customer/teetime";
import ClientPortalTeetimeFilters from "../../ClientPortal/Home/ClientPortalTeetimeFilters";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";

interface IFilterState {
  players: string;
  holes: string;
  carts: string;
}

interface ITeeTimeCard {
  id: number;
  convertedTime: string;
  time: string;
  prepaid: boolean;
  quantity_remaining: number;
  holes: number[];
  cart_rule: string;
  nine_hole_price: number;
  eighteen_hole_price: number;
  dynamic_percent_variation: number;
  courseName?: string;
  date: string;
  position: number;
  divisionTitle?: string;
  blocked_type?: unknown;
  startingHole?: string;
  leagueId: number;
  course_id: number;
  facility: IFacilityPortalExtended;
  is_restricted?: boolean;
}

interface IState {
  teeTimeCards: Array<ITeeTimeCard>;
  groupedFacilities: Array<
    IFacilityPortalExtended & {
      teeTimes?: IAPIResponse<Array<ITeeTime>> | any[];
      checkInConfig?: IAPIResponse<Array<ICheckIns>> | any[];
    }
  >;
}

export default function TeeOnPortalHome() {
  const { teeOnPortalState, updateTeeOnPortal } = useTeeOnPortalContext();
  const { cancelToken, cancelRequest } = useCancelToken();
  const [filterState, setFilterState] = useState<IFilterState>({
    players: null,
    holes: null,
    carts: null,
  });
  const windowSize = useWindowSize();
  const history = useHistory();
  const initialLoad = useRef<boolean>(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [state, setState] = useState<IState>({
    teeTimeCards: undefined,
    groupedFacilities: undefined,
  });

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0 });
  }, []);

  useEffect(() => {
    if (
      teeOnPortalState?.selectedFacilities &&
      teeOnPortalState?.selectedFacilities?.length > 0 &&
      !initialLoad.current
    ) {
      initialLoad.current = true;
      void loadTeeTimes(teeOnPortalState?.selectedFacilities, cancelToken());
    }
  }, [teeOnPortalState?.selectedFacilities]);

  useEffect(() => {
    if (teeOnPortalState?.selectedDate?.date && teeOnPortalState?.selectedFacilities) {
      cancelRequest();
      void loadTeeTimes(teeOnPortalState?.selectedFacilities, cancelToken());
    }
  }, [teeOnPortalState?.selectedDate]);

  useEffect(() => {
    //Update pricing display based on holes filter selection
    if (state?.groupedFacilities && state?.teeTimeCards !== undefined) {
      let teeTimeCards: Array<ITeeTimeCard> = [];
      state?.groupedFacilities?.forEach(facility => {
        const facilityTeeTimeCards = setTeeTimeCards(
          facility?.teeTimes as IAPIResponse<Array<ITeeTime>>,
          facility?.checkInConfig as IAPIResponse<Array<ICheckIns>>,
          facility,
        );
        teeTimeCards = [...teeTimeCards, ...facilityTeeTimeCards];
      });
      teeTimeCards?.sort((currentTeetimeCard, nextTeetimeCard) =>
        currentTeetimeCard.time?.localeCompare(nextTeetimeCard.time),
      );
      setState(prevState => ({ ...prevState, teeTimeCards: teeTimeCards }));
    }
  }, [filterState.holes]);

  function handleSelectedDateChange(selectedDate: Date) {
    //Backend counts Sunday as day 7
    const dayOfWeekNumber = moment(selectedDate).day() === 0 ? 7 : moment(selectedDate).day();
    updateTeeOnPortal({ selectedDate: { date: selectedDate, dayOfWeekNumber: dayOfWeekNumber } });
    //load tee times...
  }

  async function loadTeeTimes(selectedFacilities: Array<IFacilityPortalExtended>, cancelToken?: CancelToken) {
    //Reset tee time cards & check ins
    setState(prevState => ({ ...prevState, teeTimeCards: undefined, checkIns: [] }));

    let facilities = [...selectedFacilities];
    let teeTimesRes: Array<IAPIResponse<Array<ITeeTime>>>;
    const missingFacilityTeeSheetSettings = facilities?.filter(facility => !facility?.teeSheetSettings);
    const userActive = !!teeOnPortalState?.user;

    //User active -> customer endpoints
    if (userActive) {
      if (missingFacilityTeeSheetSettings?.length > 0) {
        const facilityTeeSheetSettingsRes = await Promise.all(
          missingFacilityTeeSheetSettings?.map(facility =>
            GetCustomerTeeSheetSettings({ facility_id: facility?.id }, false, cancelToken),
          ),
        );
        if (facilityTeeSheetSettingsRes && facilityTeeSheetSettingsRes?.some(res => res?.status !== StatusCode?.OK)) {
          if (cancelToken && cancelToken?.reason) {
            return;
          }
          dispatch(showError(t("Error getting tee sheet settings")));
        } else {
          const teeSheetSettings = facilityTeeSheetSettingsRes?.map(res => res?.data);
          const updatedFacilities = [...facilities]?.map(facility => {
            const foundSettings = teeSheetSettings?.find(settings => settings?.facility_id === facility?.id);
            if (foundSettings) {
              return { ...facility, teeSheetSettings: foundSettings };
            } else {
              return facility;
            }
          });
          facilities = updatedFacilities;
          updateTeeOnPortal({ selectedFacilities: updatedFacilities });
        }
      }

      teeTimesRes = await Promise.all(
        facilities?.map(facility =>
          GetTeeTimesCustomer(
            {
              facility_id: facility?.id,
              date: teeOnPortalState?.selectedDate?.date,
              extended: true,
              turn_tee_time: true,
            },
            false,
            cancelToken,
          ),
        ),
      );
      if (teeTimesRes && teeTimesRes?.some(res => res?.status !== StatusCode?.OK)) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
      }
    } else {
      //User not active -> Guest endpoints
      if (missingFacilityTeeSheetSettings?.length > 0) {
        const facilityTeeSheetSettingsRes = await Promise.all(
          missingFacilityTeeSheetSettings?.map(facility =>
            GetGuestTeeSheetSettings({ facility_id: facility?.id }, false, cancelToken),
          ),
        );
        if (facilityTeeSheetSettingsRes && facilityTeeSheetSettingsRes?.some(res => res?.status !== StatusCode?.OK)) {
          if (cancelToken && cancelToken?.reason) {
            return;
          }
          dispatch(showError(t("Error getting tee sheet settings")));
        } else {
          const teeSheetSettings = facilityTeeSheetSettingsRes?.map(res => res?.data);
          const updatedFacilities = [...facilities]?.map(facility => {
            const foundSettings = teeSheetSettings?.find(settings => settings?.facility_id === facility?.id);
            if (foundSettings) {
              return { ...facility, teeSheetSettings: foundSettings };
            } else {
              return facility;
            }
          });
          facilities = updatedFacilities;
          updateTeeOnPortal({ selectedFacilities: updatedFacilities });
        }
      }
      teeTimesRes = await Promise.all(
        facilities?.map(facility =>
          GetTeeTimesGuest(
            {
              facility_id: facility?.id,
              date: teeOnPortalState?.selectedDate?.date,
              extended: true,
              turn_tee_time: true,
            },
            false,
            cancelToken,
          ),
        ),
      );

      if (teeTimesRes && teeTimesRes?.some(res => res?.status !== StatusCode?.OK)) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
      }
    }

    const groupedFacilities = facilities?.map(facility => {
      const foundTeeTimes =
        teeTimesRes && teeTimesRes?.length > 0
          ? teeTimesRes?.find(
              teetime =>
                teetime?.status === StatusCode?.OK && teetime?.data?.some(data => data?.facility_id === facility?.id),
            )
          : undefined;
      return {
        ...facility,
        checkInConfig: facility?.teeSheetSettings?.display_online_pricing ? undefined : null,
        teeTimes: foundTeeTimes ?? [],
      };
    });

    let teeTimeCards: Array<ITeeTimeCard> = [];
    groupedFacilities?.forEach(facility => {
      const facilityTeeTimeCards = setTeeTimeCards(
        facility?.teeTimes as IAPIResponse<Array<ITeeTime>>,
        facility?.checkInConfig as IAPIResponse<Array<ICheckIns>>,
        facility,
      );
      teeTimeCards = [...teeTimeCards, ...facilityTeeTimeCards];
    });
    teeTimeCards?.sort((currentTeetimeCard, nextTeetimeCard) =>
      currentTeetimeCard.time?.localeCompare(nextTeetimeCard.time),
    );
    setState(prevState => ({ ...prevState, teeTimeCards: teeTimeCards, groupedFacilities: groupedFacilities }));

    // Load check ins for pricing in the background
    void loadCheckIns(facilities, userActive, cancelToken, groupedFacilities);
  }

  async function loadCheckIns(
    facilities: Array<IFacilityPortalExtended>,
    userActive: boolean,
    cancelToken: CancelToken,
    groupedFacilities: Array<
      IFacilityPortalExtended & {
        teeTimes?: IAPIResponse<Array<ITeeTime>> | any[];
        checkInConfig?: IAPIResponse<Array<ICheckIns>> | any[];
      }
    >,
  ) {
    let checkInConfigRes: IAPIResponse<ICheckIns[]>[] = [];
    const facilitiesRequiredCheckIns = facilities?.filter(
      facility => facility?.teeSheetSettings?.display_online_pricing,
    );
    if (facilitiesRequiredCheckIns?.length > 0) {
      // User is logged in
      if (userActive) {
        checkInConfigRes = await Promise.all(
          facilitiesRequiredCheckIns?.map(facility =>
            GetCustomerCheckIns(
              { facility_id: facility?.id, day_of_week_number: teeOnPortalState?.selectedDate?.dayOfWeekNumber },
              false,
              cancelToken,
            ),
          ),
        );
      } else {
        // Guest
        checkInConfigRes = await Promise.all(
          facilitiesRequiredCheckIns?.map(facility =>
            GetGuestCheckIns(
              { facility_id: facility?.id, day_of_week_number: teeOnPortalState?.selectedDate?.dayOfWeekNumber },
              false,
              cancelToken,
            ),
          ),
        );
      }
      if (checkInConfigRes && checkInConfigRes?.some(res => res?.status !== StatusCode?.OK)) {
        if (cancelToken && cancelToken?.reason) {
          return;
        }
        dispatch(showError(t("Error getting pricing")));
      }

      const updatedGroupedFacilities = groupedFacilities?.map(facility => {
        const foundConfig =
          checkInConfigRes && checkInConfigRes?.length > 0
            ? checkInConfigRes?.find(
                config =>
                  config?.status === StatusCode?.OK && config?.data?.some(data => data?.facility_id === facility?.id),
              )
            : undefined;
        return {
          ...facility,
          checkInConfig: foundConfig ? foundConfig : null,
        };
      });

      let teeTimeCards: Array<ITeeTimeCard> = [];
      updatedGroupedFacilities?.forEach(facility => {
        const facilityTeeTimeCards = setTeeTimeCards(
          facility?.teeTimes as IAPIResponse<Array<ITeeTime>>,
          facility?.checkInConfig,
          facility,
        );
        teeTimeCards = [...teeTimeCards, ...facilityTeeTimeCards];
      });
      teeTimeCards?.sort((currentTeetimeCard, nextTeetimeCard) =>
        currentTeetimeCard.time?.localeCompare(nextTeetimeCard.time),
      );
      setState(prevState => ({
        ...prevState,
        teeTimeCards: teeTimeCards,
        groupedFacilities: updatedGroupedFacilities,
      }));
    }
  }

  function setTeeTimeCards(
    teeTimesRes: IAPIResponse<Array<ITeeTime>>,
    checkInRes: IAPIResponse<Array<ICheckIns>>,
    facility: IFacilityPortalExtended,
  ) {
    const teeTimes = teeTimesRes?.data ? [...teeTimesRes.data] : [];
    const teetimeCards: ITeeTimeCard[] = [];
    const checkInsData = checkInRes?.data ? [...checkInRes.data] : checkInRes === undefined ? undefined : [];
    if (teeTimes) {
      for (let i = 0; i < teeTimes?.length; i++) {
        if (invalidTeeTime(teeTimes[i])) {
          continue;
        }
        const teetimeData = teeTimes[i];
        const startTime = convertTime(teetimeData.start_time);

        const holes = teetimeData.divisions_available.map(divisionAvailable => {
          if (divisionAvailable === 1) {
            return 9;
          } else if (divisionAvailable === 2) {
            return 18;
          }
        });

        let eighteenHolePrice: number;
        let nineHolePrice: number;
        if (checkInsData) {
          checkInsData?.every(checkIn => {
            if (eighteenHolePrice && nineHolePrice) {
              return false;
            } else {
              const startTime = moment(checkIn?.pricing_sheet?.start_time, "HH:mm");
              const endTime = moment(checkIn?.pricing_sheet?.end_time, "HH:mm");
              const checkInCourseId = checkIn?.pricing_sheet?.course_id;
              if (
                (moment(teetimeData?.start_time, "HH:mm").isBetween(startTime, endTime) ||
                  moment(teetimeData?.start_time, "HH:mm").isSame(startTime) ||
                  moment(teetimeData?.start_time, "HH:mm").isSame(endTime)) &&
                checkHolesSelected(checkIn?.holes) &&
                checkInCourseId === teetimeData?.course_id
              ) {
                if (checkIn?.holes === 18 && holes?.includes(18) && !eighteenHolePrice) {
                  const priceOverride = checkIn?.variant?.inventory_levels?.find(
                    level => level?.facility_id === facility?.id,
                  )?.price_override;
                  eighteenHolePrice = priceOverride ?? checkIn?.variant?.price;
                } else if (checkIn?.holes === 9 && holes?.includes(9) && !nineHolePrice) {
                  const priceOverride = checkIn?.variant?.inventory_levels?.find(
                    level => level?.facility_id === facility?.id,
                  )?.price_override;
                  nineHolePrice = priceOverride ?? checkIn?.variant?.price;
                }
              }
              return true;
            }
          });
        }

        if (teetimeData.quantity_remaining >= 1) {
          const teetimeCard: ITeeTimeCard = {
            id: teetimeData.id,
            convertedTime: startTime,
            time: teetimeData.start_time,
            prepaid: teeTimes[i].pre_paid_required,
            quantity_remaining: teetimeData.quantity_remaining,
            holes: holes,
            cart_rule: teetimeData.cart_rule,
            dynamic_percent_variation: teetimeData.dynamic_percent_variation,
            eighteen_hole_price:
              eighteenHolePrice && !!facility?.teeSheetSettings?.display_online_pricing
                ? eighteenHolePrice
                : checkInsData === undefined
                ? undefined
                : null,
            nine_hole_price:
              nineHolePrice && !!facility?.teeSheetSettings?.display_online_pricing
                ? nineHolePrice
                : checkInsData === undefined
                ? undefined
                : null,
            courseName: facility?.courses?.length > 1 ? teetimeData.course_full_name : undefined,
            date: teetimeData.date,
            position: teetimeData.position,
            divisionTitle: teetimeData.division_title,
            blocked_type: teetimeData.blocked_type,
            startingHole: teetimeData.start_hole,
            leagueId: teetimeData.league_id,
            course_id: teetimeData?.course_id,
            facility: facility,
            is_restricted: teetimeData?.is_restricted,
          };

          teetimeCards.push(teetimeCard);
        }
      }
      return teetimeCards;
    }
  }

  function filterTeeTimeCards(teeTimeCards: ITeeTimeCard[]) {
    return teeTimeCards?.filter(teeTimeCard => {
      if (
        teeTimeCard?.facility?.teeSheetSettings?.single_bookings === "allow_within_group" &&
        parseInt(filterState?.players) === 1
      ) {
        return teeTimeCard.quantity_remaining < 4;
      } else {
        return (
          (filterState.players === null || teeTimeCard.quantity_remaining >= parseInt(filterState.players)) &&
          (filterState.holes === null || teeTimeCard.holes.includes(parseInt(filterState.holes))) &&
          (filterState.carts === null ||
            ((teeTimeCard.cart_rule === "none" || teeTimeCard.cart_rule === "optional") &&
              filterState.carts === "no") ||
            ((teeTimeCard.cart_rule === "required" || teeTimeCard.cart_rule === "optional") &&
              filterState.carts === "yes")) &&
          (teeTimeCard?.facility?.teeSheetSettings?.single_bookings === "allow" ||
            teeTimeCard?.facility?.teeSheetSettings?.single_bookings === "allow_within_group" ||
            (teeTimeCard?.facility?.teeSheetSettings?.single_bookings === "none" &&
              teeTimeCard?.quantity_remaining >= 2))
        );
      }
    });
  }

  function invalidTeeTime(teeTime: ITeeTime) {
    return (
      teeTime?.divisions_available?.length === 1 && teeTime?.divisions_available[0] === 2 && !teeTime?.turn_tee_time_id
    );
  }

  function checkHolesSelected(holes: number) {
    switch (filterState.holes) {
      case "18":
        if (holes === 18) {
          return true;
        }
        break;
      case "9":
        if (holes === 9) {
          return true;
        }
        break;
      case "any":
      case null:
        return true;
    }
    return false;
  }

  async function checkTeeTime(date: string, teeTimeId: number, facility: IFacilityPortalExtended) {
    const teeTimeFacility =
      teeOnPortalState?.selectedFacilities?.find(foundFacility => foundFacility?.id === facility?.id) ?? facility;
    const params = {
      date: date,
      id: teeTimeId,
      facility_id: facility?.id,
      turn_tee_time: true,
      extended: true,
    };
    let selectedTeeTime: ITeeTime & { formattedDate?: string; formattedStartTime?: string };

    // If user is active, load auth route for tee time
    let teeTimeRes;
    if (teeOnPortalState?.user) {
      teeTimeRes = await GetTeeTimesCustomer(params, true);
    } else {
      teeTimeRes = await GetTeeTimesGuest(params, true);
    }

    if (teeTimeRes.status === StatusCode.GONE) {
      dispatch(showError(t("Tee time is currently being booked by another player")));
      return;
    }

    if (teeTimeRes.status === StatusCode.LENGTH_REQUIRED) {
      dispatch(showError(t("Cannot book a tee time in the past")));
      return;
    }

    if (teeTimeRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error retrieving tee time")));
      return;
    }

    if (teeTimeRes.data[0].quantity_remaining === 0) {
      dispatch(showError(t("Tee time has no spots available, please choose another")));
      return;
    }

    selectedTeeTime = teeTimeRes?.data[0];

    const formattedStartTime = convertTime(selectedTeeTime?.start_time ?? "");
    const formattedDate = formatDateFromString(String(date));
    selectedTeeTime = { ...selectedTeeTime, formattedDate, formattedStartTime };

    if (teeOnPortalState?.user) {
      //If not null - User has access to their own locked teetime
      if (teeTimeRes.data[0].lock !== null) {
        const tempCurrentDate = new Date().toUTCString();
        const currentTempTime = new Date(tempCurrentDate);
        const currentTime = new Date(currentTempTime).getTime();

        const tempString = String(teeTimeRes.data[0].lock.start_time) + "Z";

        const lockStartDate = new Date(tempString);

        const tempExpiryString = String(teeTimeRes.data[0].lock.expire_time) + "Z";
        const lockoutTempExpiry = new Date(tempExpiryString);

        const lockStartTime = lockStartDate.getTime();
        const lockoutExpiryTime = lockoutTempExpiry.getTime();

        const lockoutDuration = lockoutExpiryTime - currentTime;

        updateTeeOnPortal({
          selectedTeeTime: {
            ...teeOnPortalState?.selectedTeeTime,
            teeTime: selectedTeeTime,
            facility: teeTimeFacility,
            lock: { lockoutDuration, lockStartTime, expireTime: selectedTeeTime?.lock?.expire_time },
          },
        });

        const bookingAddress = `/booking/teetimes/book?id=${teeTimeId}&date=${date}`;
        history.push(bookingAddress);
      } else {
        const bookingLockRes = await LockTeeTime({ tee_time_id: teeTimeRes.data[0].id }, true);

        if (bookingLockRes.status !== StatusCode.OK) {
          dispatch(showError(bookingLockRes.data.message as string));
          return;
        }

        if (bookingLockRes.data.message === "Tee time already locked by user") {
          dispatch(showError(t("Booking is locked by another user, please try again in a few minutes..")));
          console.log("Booking is locked by another user");
          return;
        }

        const tempCurrentDate = new Date().toUTCString();
        const currentTempTime = new Date(tempCurrentDate);
        const currentTime = new Date(currentTempTime).getTime();

        const tempString = String(bookingLockRes.data.start_time) + "Z";

        const lockStartDate = new Date(tempString);

        const tempExpiryString = String(bookingLockRes.data.expire_time) + "Z";
        const lockoutTempExpiry = new Date(tempExpiryString);

        const lockStartTime = lockStartDate.getTime();
        const lockoutExpiryTime = lockoutTempExpiry.getTime();

        const lockoutDuration = lockoutExpiryTime - currentTime;

        updateTeeOnPortal({
          selectedTeeTime: {
            ...teeOnPortalState?.selectedTeeTime,
            teeTime: selectedTeeTime,
            facility: teeTimeFacility,
            lock: { lockoutDuration, lockStartTime, expireTime: bookingLockRes?.data?.expire_time },
          },
        });

        const bookingAddress = `/booking/teetimes/book?id=${teeTimeId}&date=${date}`;
        history.push(bookingAddress);
      }
    } else {
      updateTeeOnPortal({
        selectedTeeTime: {
          ...teeOnPortalState?.selectedTeeTime,
          teeTime: selectedTeeTime,
          facility: teeTimeFacility,
        },
      });
      const loginAddress = `/booking/login?id=${teeTimeId}&date=${date}&facility=${facility?.short_name}`;
      history.push(loginAddress);
    }
  }

  const teeTimeCards = useMemo(() => {
    return state?.teeTimeCards !== undefined
      ? state?.teeTimeCards
        ? filterTeeTimeCards(state?.teeTimeCards)
        : []
      : undefined;
  }, [state?.teeTimeCards, filterState.carts, filterState.players]);

  return (
    <div className="tee-on-portal-home">
      <TeeOnPortalFacilities
        loadTeeTimes={async updatedFacilities => {
          cancelRequest();
          await loadTeeTimes(updatedFacilities, cancelToken());
        }}
      />
      <div className="tee-on-portal-home-date-container">
        <DatePickerInput
          months={1}
          position="left"
          startingDate={teeOnPortalState?.selectedDate?.date}
          setStartingDate={handleSelectedDateChange}
          disablePastDays
        />
        {windowSize.width <= MOBILE_WIDTH ? (
          <ClientPortalTeetimeFilters
            filters={{
              selectedCart: filterState.carts,
              selectedHoles: filterState.holes,
              selectedPlayers: filterState.players,
            }}
            updateFilters={filters =>
              setFilterState(prevState => ({
                ...prevState,
                carts: filters?.selectedCart,
                holes: filters?.selectedHoles,
                players: filters?.selectedPlayers,
              }))
            }
            showCartFilters={true}
          />
        ) : (
          <div className="tee-on-portal-home-date-bar">
            <DateBar
              numberOfDays={8}
              selectedDate={teeOnPortalState?.selectedDate?.date}
              onClick={handleSelectedDateChange}
            />
          </div>
        )}
      </div>
      <div className="tee-on-portal-home-sections-parent-container">
        <div className="tee-on-portal-home-sections-container">
          <div className="tee-on-portal-home-filters-outer">
            <div className="tee-on-portal-home-filters">
              <div>
                <div className="tee-on-portal-home-filters-header">{"Number of Players"}</div>
                <Radio.Group
                  name="players"
                  onChange={(selectedPlayers: string) =>
                    setFilterState(prevState => ({
                      ...prevState,
                      players: selectedPlayers === "any" ? null : selectedPlayers,
                    }))
                  }
                  value={filterState.players ?? "any"}
                >
                  {playerOptions(t).map(player => {
                    return (
                      <Radio.Button key={`players_${player.value}`} value={player.value} disabled={player?.disabled}>
                        {player.title}
                      </Radio.Button>
                    );
                  })}
                </Radio.Group>
              </div>
              <div>
                <div className="tee-on-portal-home-filters-header">{t("Holes")}</div>
                <Radio.Group
                  name="holes"
                  onChange={(selectedHoles: string) =>
                    setFilterState(prevState => ({
                      ...prevState,
                      holes: selectedHoles === "any" ? null : selectedHoles,
                    }))
                  }
                  value={filterState.holes ?? "any"}
                >
                  {holeOptions.map(hole => {
                    return (
                      <Radio.Button key={`holes_${hole.value}`} value={hole.value}>
                        <span>{hole.title}</span>
                        {hole.value !== "any" ? (
                          <FontAwesomeIcon className="ml-2" icon={["far", "golf-flag-hole"]} />
                        ) : null}
                      </Radio.Button>
                    );
                  })}
                </Radio.Group>
              </div>
              <div>
                <div className="tee-on-portal-home-filters-header">{t("Cart")}</div>
                <Radio.Group
                  name="carts"
                  onChange={(selectedCart: string) =>
                    setFilterState(prevState => ({
                      ...prevState,
                      carts: selectedCart === "any" ? null : selectedCart,
                    }))
                  }
                  value={filterState.carts ?? "any"}
                >
                  {cartOptions.map(cartOption => {
                    return (
                      <Radio.Button key={`cart_${cartOption.value}`} value={cartOption.value}>
                        {cartOption.title}
                      </Radio.Button>
                    );
                  })}
                </Radio.Group>
              </div>
            </div>
          </div>
          <div className="tee-on-portal-home-tee-times">
            <div className="tee-on-portal-home-tee-times-cards">
              {state?.teeTimeCards?.length === 0 && (
                <Callout type="error" title={t("No Tee Times")} content={t("There are no tee times available")} />
              )}
              {teeTimeCards === undefined
                ? [...Array(3)]?.map((_, index) => {
                    return (
                      <LoaderCard
                        key={index}
                        textRows={4}
                        customCSSProperties={{ width: "100%", height: "190px" }}
                        textRowSpacing={25}
                      />
                    );
                  })
                : teeTimeCards?.map((teetime, index) => (
                    <CustomerTeeTimeCard
                      key={index}
                      time={teetime.convertedTime}
                      isPrepaid={teetime.prepaid}
                      isShotgun={teetime.blocked_type === "shotgun"}
                      spotsRemaining={teetime.quantity_remaining}
                      holes={teetime.holes}
                      cartRule={teetime?.facility?.teeSheetSettings?.enable_cart_bookings ? teetime.cart_rule : "none"}
                      eighteenHolePrice={teetime.eighteen_hole_price}
                      nineHolePrice={teetime.nine_hole_price}
                      dynamicPercentVariation={teetime.dynamic_percent_variation}
                      onClick={() => void checkTeeTime(teetime.date, teetime.id, teetime?.facility)}
                      courseName={teetime?.facility?.courses?.length > 1 ? `Course: ${teetime?.courseName}` : null}
                      divisionTitle={teetime.divisionTitle}
                      singleBookings={teetime?.facility?.teeSheetSettings?.single_bookings}
                      startingHole={teetime.startingHole}
                      isLeague={!!teetime.leagueId}
                      facilityName={teetime?.facility?.long_name}
                      restricted={teetime?.is_restricted}
                    />
                  ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
